import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface Props {
  question: string;
  /**
   * "mobile"  – inline card, top of mobile quiz column
   * "desktop" – fixed/centered card overlaying the classroom art
   */
  variant?: "mobile" | "desktop";
}

/**
 * Universal quiz question container.
 *
 * - Adds a strong dark/glass underlay so questions are always readable
 *   against the classroom background art.
 * - Adaptive sizing for short / medium / long questions.
 * - For very long questions: capped height + internal scroll, plus an
 *   "Show full" / "Minimize" toggle.
 * - Responsive typography and width caps for desktop reading comfort.
 */
export default function QuestionCard({ question, variant = "mobile" }: Props) {
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isDesktop = variant === "desktop";

  // Collapsed cap differs by variant.
  // Desktop card is anchored at top:5% and answer buttons begin at ~22% of
  // the viewport, so the card has roughly 17vh of safe space. We always
  // render at a tight cap on desktop and rely on internal scrolling so the
  // card can never visually collide with the answer overlay — even with
  // very long questions, browser zoom (125/150%), or short viewports.
  const COLLAPSED_MAX_PX = isDesktop ? 200 : 168;
  // Long-content heuristic so the toggle shows even before measurement settles.
  const longThreshold = isDesktop ? 280 : 220;
  const isLongHeuristic = question.length > longThreshold;

  useLayoutEffect(() => {
    if (!textRef.current) return;
    const el = textRef.current;
    const prev = el.style.maxHeight;
    el.style.maxHeight = "none";
    const full = el.scrollHeight;
    el.style.maxHeight = prev;
    setOverflowing(full > COLLAPSED_MAX_PX + 8 || isLongHeuristic);
  }, [question, isLongHeuristic, COLLAPSED_MAX_PX]);

  useEffect(() => {
    setExpanded(false);
  }, [question]);

  // On desktop we never expose an expand toggle — the card is height-locked
  // and any overflow scrolls inside it. On mobile the toggle still expands
  // the in-flow card up to 60vh.
  const showToggle = overflowing && !isDesktop;
  const collapsed = showToggle && !expanded;
  const desktopScroll = isDesktop && overflowing;

  const cardClass = isDesktop
    ? // Desktop: wider card, larger padding, capped width for readable line length.
      // overflow-hidden keeps long content scrolling INSIDE the card, never below.
      "relative overflow-hidden rounded-[1.25rem] border border-cyan-300/35 bg-gradient-to-br from-cyan-400/10 via-slate-900/35 to-violet-500/10 backdrop-blur-2xl px-7 py-5 shadow-[0_20px_60px_-18px_hsl(190_90%_60%/0.5)] max-h-[220px]"
    : "relative overflow-hidden rounded-[1.25rem] border border-cyan-300/35 bg-gradient-to-br from-cyan-400/10 via-slate-900/35 to-violet-500/10 backdrop-blur-2xl px-5 py-4 shadow-[0_14px_40px_-14px_hsl(190_90%_60%/0.45)]";

  const textClass = isDesktop
    ? "font-heading font-bold text-foreground leading-relaxed break-words text-left text-xl lg:text-2xl xl:text-[26px]"
    : "font-heading font-bold text-foreground leading-relaxed break-words text-left text-[15px] sm:text-base";

  return (
    <div
      className={
        isDesktop
          ? "relative w-full max-w-4xl mx-auto px-1"
          : "relative mb-4 shrink-0"
      }
    >
      {/* Localized contrast layer behind the card to protect readability
          over busy artwork without darkening the whole page. */}
      <div
        aria-hidden
        className={
          isDesktop
            ? "pointer-events-none absolute -inset-x-6 -inset-y-6 -z-10 rounded-[2rem] bg-gradient-to-b from-background/85 via-background/60 to-transparent blur-lg"
            : "pointer-events-none absolute -inset-x-2 -inset-y-3 -z-10 rounded-3xl bg-gradient-to-b from-background/80 via-background/55 to-transparent blur-md"
        }
      />

      <div className={cardClass} role="group" aria-label="Quiz question">
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/60 to-transparent" />
        <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[1.25rem] ring-1 ring-inset ring-white/10" />
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-gradient-to-b from-white/[0.06] to-transparent" />
        <span aria-hidden className="pointer-events-none absolute -inset-px rounded-[1.25rem] [background:radial-gradient(120%_60%_at_50%_-10%,hsl(190_90%_60%/0.18),transparent_60%)]" />
        <div className="mb-2 flex items-center justify-between gap-3">
          <span
            className={
              isDesktop
                ? "text-[11px] font-heading font-semibold uppercase tracking-[0.22em] text-accent/90"
                : "text-[10px] font-heading font-semibold uppercase tracking-[0.18em] text-accent/90"
            }
          >
            Question
          </span>
          {showToggle && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className={
                isDesktop
                  ? "flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-3 py-1.5 text-[11px] font-heading font-semibold uppercase tracking-wider text-accent transition-colors hover:bg-accent/20 active:bg-accent/30 min-h-[32px]"
                  : "flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2.5 py-1 text-[10px] font-heading font-semibold uppercase tracking-wider text-accent transition-colors hover:bg-accent/20 active:bg-accent/30 min-h-[28px]"
              }
              aria-expanded={expanded}
            >
              {expanded ? (
                <>
                  Minimize <ChevronUp className={isDesktop ? "h-3.5 w-3.5" : "h-3 w-3"} />
                </>
              ) : (
                <>
                  Show full <ChevronDown className={isDesktop ? "h-3.5 w-3.5" : "h-3 w-3"} />
                </>
              )}
            </button>
          )}
        </div>

        <p
          ref={textRef}
          className={`${textClass} transition-[max-height] duration-300 ease-out ${
            collapsed
              ? "overflow-hidden"
              : showToggle || desktopScroll
              ? "overflow-y-auto pr-3 scrollbar-thin"
              : ""
          }`}
          style={{
            maxHeight: collapsed
              ? `${COLLAPSED_MAX_PX}px`
              : isDesktop
              ? // Desktop hard cap — internal scroll only. Never grows into
                // the answer button overlay. Leaves room for the label row
                // within the 220px card cap.
                `150px`
              : showToggle
              ? "60vh"
              : undefined,
          }}
        >
          {question}
        </p>

        {collapsed && (
          <div
            aria-hidden
            className="pointer-events-none -mt-6 h-6 rounded-b-2xl bg-gradient-to-t from-background/95 to-transparent"
          />
        )}
      </div>
    </div>
  );
}
